import { Box, Flex, Text, Avatar } from '@mantine/core';
import moment from 'moment';
import type { SlackMessage } from '../../../api/slackConversation';
import { formatSlackMessageText } from './slack-text-utils';
import { ThreadReplies } from './ThreadReplies';

const EXCERPT_MESSAGE_BG = '#FFFDD4';

interface MessageProps {
  message: SlackMessage;
  isHighlighted?: boolean;
  excerptRef?: React.Ref<HTMLDivElement>;
  isReply?: boolean;
  excerptRefIndexByMessageTs?: Map<string, number>;
  excerptRefsArray?: React.MutableRefObject<(HTMLDivElement | null)[]>;
  excerptTexts?: string[];
  excerptTextsByMessageTs?: Map<string, string[]>;
}

function formatTime(timestamp: string): string {
  const d = moment(timestamp);
  return d.format('h:mm A');
}

function getDisplayName(message: SlackMessage): string {
  return (
    message.userInfo?.displayName ||
    message.userInfo?.realName ||
    message.userName ||
    message.userInfo?.name ||
    'Unknown'
  );
}

export function Message({ message, isHighlighted, excerptRef, isReply, excerptRefIndexByMessageTs, excerptRefsArray, excerptTexts, excerptTextsByMessageTs }: MessageProps) {
  const displayName = getDisplayName(message);
  const timeStr = formatTime(message.timestamp);
  const contentSegments = formatSlackMessageText(message.text || '', excerptTexts);

  return (
    <Box
      ref={excerptRef}
      bg={isHighlighted ? EXCERPT_MESSAGE_BG : 'transparent'}
      py="xs"
      px="sm"
      sx={{ '&:hover': { backgroundColor: isReply ? undefined : 'rgba(0,0,0,0.02)' } }}
    >
      <Flex gap="sm" align="flex-start">
        <Avatar
          size={isReply ? 'sm' : 'md'}
          radius="xl"
          color="blue"
          styles={{ root: { flexShrink: 0 } }}
        >
          {displayName.slice(0, 2).toUpperCase()}
        </Avatar>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Flex gap="xs" align="baseline" wrap="nowrap">
            <Text size="sm" fw={700} color="#000" component="span">
              {displayName}
            </Text>
            <Text size="xs" c="dimmed" component="span">
              {timeStr}
            </Text>
          </Flex>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {contentSegments}
          </Text>
          {message.replies && message.replies.length > 0 && (
            <ThreadReplies
              replies={message.replies}
              excerptRefIndexByMessageTs={excerptRefIndexByMessageTs}
              excerptRefsArray={excerptRefsArray}
              excerptTextsByMessageTs={excerptTextsByMessageTs}
            />
          )}
        </Box>
      </Flex>
    </Box>
  );
}
